<template>
  <div class="modal fade" id="employeeSalaryModal" tabindex="-1" aria-labelledby="employeeSalaryModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="employeeSalaryModalLabel">
            {{ isEditMode ? 'Update Employee Salary' : 'Employee Salary Details' }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary me-2" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading employee salary information...</p>
          </div>

          <div v-else>
            <!-- Employee information header -->
            <div class="card mb-4">
              <div class="card-body">
                <div class="d-flex align-items-center">
                  <div class="flex-shrink-0 me-3">
                    <div class="avatar-md">
                      <span class="avatar-title bg-light text-primary rounded-circle">
                        <i class="mdi mdi-account font-size-24"></i>
                      </span>
                    </div>
                  </div>
                  <div class="flex-grow-1">
                    <h5 class="font-size-16 mb-1">{{ employeeData.name }}</h5>
                    <p class="text-muted mb-1">{{ employeeData.designation }} | {{ employeeData.department }}</p>
                    <p class="text-muted mb-0">
                      <small>
                        <i class="mdi mdi-calendar-account me-1"></i> Joined: {{ formatDate(employeeData.hire_date) }}
                        <span class="mx-2">|</span>
                        <i class="mdi mdi-clock-time-five-outline me-1"></i> Service: {{ calculateServiceYears(employeeData.hire_date) }} years
                      </small>
                    </p>
                  </div>
                  <div class="ms-auto">
                    <button
                        v-if="!isEditMode"
                        @click="toggleEditMode"
                        class="btn btn-primary btn-sm"
                    >
                      <i class="mdi mdi-pencil me-1"></i> Edit
                    </button>
                    <button
                        v-else
                        @click="toggleEditMode"
                        class="btn btn-secondary btn-sm"
                    >
                      <i class="mdi mdi-close me-1"></i> Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Mandatory Deductions Alert -->
            <div v-if="isEditMode && !hasExistingSalary && displayMandatoryDeductions.length > 0" class="alert alert-info mb-4">
              <h6 class="alert-heading"><i class="mdi mdi-information-outline me-1"></i>Automatic Mandatory Deductions</h6>
              <p class="mb-0">The following mandatory deductions will be automatically applied when you save:</p>
              <ul class="mb-0 mt-2">
                <li v-for="deduction in displayMandatoryDeductions" :key="deduction.id">
                  <strong>{{ deduction.deduction_name }}</strong>
                  <span v-if="deduction.description"> - {{ deduction.description }}</span>
                </li>
              </ul>
            </div>

            <!-- Auto Pay Grade Selection Alert -->
            <div v-if="isEditMode && autoSelectedPayGrade" class="alert alert-success mb-4">
              <h6 class="alert-heading"><i class="mdi mdi-check-circle me-1"></i>Pay Grade Auto-Selected</h6>
              <p class="mb-0">
                Based on the salary amount of <strong>{{ formatCurrency(salaryForm.basic_salary) }}</strong>,
                the pay grade <strong>"{{ autoSelectedPayGrade.grade_name }}"</strong> has been automatically selected.
                <br>
                <small class="text-muted">
                  Range: {{ formatCurrency(autoSelectedPayGrade.min_salary) }} - {{ formatCurrency(autoSelectedPayGrade.max_salary) }}
                </small>
              </p>
            </div>

            <!-- Salary details form/display -->
            <form @submit.prevent="saveSalary">
              <div class="row">
                <div class="col-md-6">
                  <div class="card h-100">
                    <div class="card-header">
                      <h5 class="card-title mb-0">Salary Information</h5>
                    </div>
                    <div class="card-body">
                      <div class="mb-3">
                        <label class="form-label">Basic Salary <span class="text-danger">*</span></label>
                        <div class="input-group">
                          <span class="input-group-text">{{ getCurrencySymbol() }}</span>
                          <input
                              type="number"
                              class="form-control"
                              v-model="salaryForm.basic_salary"
                              @input="onBasicSalaryChange"
                              :readonly="!isEditMode"
                              required
                              step="0.01"
                              min="0"
                              placeholder="Enter basic salary amount"
                          >
                        </div>
                        <div v-if="isEditMode && salaryForm.basic_salary && suggestedPayGrade" class="mt-2">
                          <small class="text-success">
                            <i class="mdi mdi-lightbulb-outline me-1"></i>
                            Suggested Pay Grade: <strong>{{ suggestedPayGrade.grade_name }}</strong>
                            ({{ formatCurrency(suggestedPayGrade.min_salary) }} - {{ formatCurrency(suggestedPayGrade.max_salary) }})
                          </small>
                        </div>
                        <div v-else-if="isEditMode && salaryForm.basic_salary && !suggestedPayGrade" class="mt-2">
                          <small class="text-warning">
                            <i class="mdi mdi-alert-outline me-1"></i>
                            No matching pay grade found for this salary amount.
                          </small>
                        </div>
                      </div>

                      <div class="mb-3">
                        <label class="form-label">Pay Grade</label>
                        <select
                            class="form-select"
                            v-model="salaryForm.pay_grade_id"
                            :disabled="!isEditMode"
                            @change="onPayGradeManualChange"
                        >
                          <option value="">No Pay Grade</option>
                          <optgroup label="Regular Salary Grades" v-if="regularPayGrades.length > 0">
                            <option
                                v-for="grade in regularPayGrades"
                                :key="grade.id"
                                :value="grade.id"
                                :class="getPayGradeOptionClass(grade)"
                            >
                              {{ grade.grade_name }} ({{ formatCurrency(grade.min_salary) }} - {{ formatCurrency(grade.max_salary) }})
                            </option>
                          </optgroup>
                          <optgroup label="Other Grades" v-if="dsaPayGrades.length > 0">
                            <option
                                v-for="grade in dsaPayGrades"
                                :key="grade.id"
                                :value="grade.id"
                                :class="getPayGradeOptionClass(grade)"
                            >
                              {{ grade.grade_name }} ({{ formatCurrency(grade.min_salary) }})
                            </option>
                          </optgroup>
                        </select>
                        <div v-if="isEditMode && salaryForm.pay_grade_id && selectedPayGrade && !isSalaryInPayGradeRange" class="mt-2">
                          <small class="text-danger">
                            <i class="mdi mdi-alert-circle-outline me-1"></i>
                            Warning: Current salary ({{ formatCurrency(salaryForm.basic_salary) }}) is outside the selected pay grade range
                            ({{ formatCurrency(selectedPayGrade.min_salary) }} - {{ formatCurrency(selectedPayGrade.max_salary) }})
                          </small>
                        </div>
                        <div v-else-if="isEditMode && salaryForm.pay_grade_id && selectedPayGrade && isSalaryInPayGradeRange" class="mt-2">
                          <small class="text-success">
                            <i class="mdi mdi-check-circle-outline me-1"></i>
                            Salary is within the pay grade range
                          </small>
                        </div>
                      </div>

                      <div class="mb-3">
                        <label class="form-label">Effective Date <span class="text-danger">*</span></label>
                        <input
                            type="date"
                            class="form-control"
                            v-model="salaryForm.effective_date"
                            :readonly="!isEditMode"
                            required
                        >
                      </div>

                      <div class="mb-3">
                        <label class="form-label">Payment Frequency</label>
                        <select
                            class="form-select"
                            v-model="salaryForm.payment_frequency"
                            :disabled="!isEditMode"
                        >
                          <option value="Monthly">Monthly</option>
                          <option value="Bi-weekly">Bi-weekly</option>
                          <option value="Weekly">Weekly</option>
                          <option value="Daily">Daily</option>
                        </select>
                      </div>

                      <div class="mb-3">
                        <label class="form-label">Currency</label>
                        <select
                            class="form-select"
                            v-model="salaryForm.currency"
                            :disabled="!isEditMode"
                        >
                          <option value="KES">Kenyan Shilling (KES)</option>
                          <option value="USD">US Dollar (USD)</option>
                          <option value="GBP">British Pound (GBP)</option>
                          <option value="EUR">Euro (EUR)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="card h-100">
                    <div class="card-header">
                      <h5 class="card-title mb-0">Payment Details</h5>
                    </div>
                    <div class="card-body">
                      <div class="mb-3">
                        <label class="form-label">Payment Method</label>
                        <select
                            class="form-select"
                            v-model="salaryForm.payment_method"
                            :disabled="!isEditMode"
                        >
                          <option value="Bank Transfer">Bank Transfer</option>
                          <option value="Check">Check</option>
                          <option value="Cash">Cash</option>
                          <option value="Mobile Money">Mobile Money</option>
                        </select>
                      </div>

                      <div class="mb-3" v-if="salaryForm.payment_method === 'Bank Transfer'">
                        <label class="form-label">Bank Name</label>
                        <input
                            type="text"
                            class="form-control"
                            v-model="salaryForm.bank_name"
                            :readonly="!isEditMode"
                        >
                      </div>

                      <div class="mb-3" v-if="salaryForm.payment_method === 'Bank Transfer'">
                        <label class="form-label">Bank Code</label>
                        <input
                            type="text"
                            class="form-control"
                            v-model="salaryForm.bank_code"
                            :readonly="!isEditMode"
                            placeholder="e.g., 01, 11, etc."
                        >
                      </div>

                      <div class="mb-3" v-if="salaryForm.payment_method === 'Bank Transfer'">
                        <label class="form-label">Account Number</label>
                        <input
                            type="text"
                            class="form-control"
                            v-model="salaryForm.account_number"
                            :readonly="!isEditMode"
                        >
                      </div>

                      <div class="mb-3" v-if="salaryForm.payment_method === 'Bank Transfer'">
                        <label class="form-label">Branch Code</label>
                        <input
                            type="text"
                            class="form-control"
                            v-model="salaryForm.branch_code"
                            :readonly="!isEditMode"
                        >
                      </div>

                      <div class="mb-3" v-if="salaryForm.payment_method === 'Mobile Money'">
                        <label class="form-label">Mobile Money Number</label>
                        <input
                            type="text"
                            class="form-control"
                            v-model="salaryForm.mobile_money_number"
                            :readonly="!isEditMode"
                        >
                      </div>

                      <div class="form-check" v-if="isEditMode">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            id="isActiveCheckbox"
                            v-model="salaryForm.is_active"
                        >
                        <label class="form-check-label" for="isActiveCheckbox">
                          Active Salary Record
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Pay Grade Information Card -->
              <div class="card mt-4" v-if="selectedPayGrade">
                <div class="card-header">
                  <h5 class="card-title mb-0">
                    <i class="mdi mdi-star-outline me-2"></i>Pay Grade Information
                  </h5>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-8">
                      <h6 class="fw-bold">{{ selectedPayGrade.grade_name }}</h6>
                      <p class="mb-2">{{ selectedPayGrade.description }}</p>
                      <div class="row">
                        <div class="col-md-4">
                          <small class="text-muted">Minimum Salary:</small>
                          <div class="fw-bold">{{ formatCurrency(selectedPayGrade.min_salary) }}</div>
                        </div>
                        <div class="col-md-4">
                          <small class="text-muted">Maximum Salary:</small>
                          <div class="fw-bold">{{ formatCurrency(selectedPayGrade.max_salary) }}</div>
                        </div>
                        <div class="col-md-4">
                          <small class="text-muted">Midpoint Salary:</small>
                          <div class="fw-bold">{{ formatCurrency(selectedPayGrade.midpoint_salary) }}</div>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="text-end">
                        <div class="position-relative">
                          <div class="progress" style="height: 20px;">
                            <div
                                class="progress-bar"
                                :class="getSalaryPositionClass()"
                                :style="{ width: getSalaryPositionPercentage() + '%' }"
                                role="progressbar"
                            >
                              {{ getSalaryPositionPercentage() }}%
                            </div>
                          </div>
                          <small class="text-muted mt-1 d-block">
                            Current salary position in range
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Salary history section -->
              <div class="card mt-4" v-if="salaryHistory.length > 0">
                <div class="card-header">
                  <h5 class="card-title mb-0">Salary History</h5>
                </div>
                <div class="card-body">
                  <div class="table-responsive">
                    <table class="table table-sm table-hover">
                      <thead class="table-light">
                      <tr>
                        <th>Effective Date</th>
                        <th>Basic Salary</th>
                        <th>Pay Grade</th>
                        <th>Change</th>
                        <th>Created By</th>
                        <th>Notes</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(history, index) in salaryHistory" :key="index">
                        <td>{{ formatDate(history.effective_date) }}</td>
                        <td>{{ formatCurrency(history.basic_salary) }}</td>
                        <td>{{ history.pay_grade_name || 'N/A' }}</td>
                        <td>
                            <span :class="getSalaryChangeClass(history, index)">
                              {{ formatSalaryChange(history, index) }}
                            </span>
                        </td>
                        <td>{{ history.created_by }}</td>
                        <td>{{ history.notes || 'N/A' }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- Allowances and deductions -->
              <div class="row mt-4">
                <div class="col-md-6">
                  <div class="card">
                    <div class="card-header d-flex justify-content-between align-items-center">
                      <h5 class="card-title mb-0">Allowances</h5>
                      <button
                          v-if="isEditMode"
                          type="button"
                          class="btn btn-sm btn-primary"
                          @click="showAddAllowanceModal"
                      >
                        <i class="mdi mdi-plus"></i>
                      </button>
                    </div>
                    <div class="card-body">
                      <div v-if="!employeeAllowances.length" class="text-center py-3">
                        <i class="mdi mdi-cash-plus text-muted font-size-24"></i>
                        <p class="mt-2 mb-0">No allowances configured</p>
                      </div>
                      <div v-else class="table-responsive">
                        <table class="table table-sm table-hover">
                          <thead class="table-light">
                          <tr>
                            <th>Type</th>
                            <th>Amount</th>
                            <th>Taxable</th>
                            <th v-if="isEditMode">Actions</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="allowance in employeeAllowances" :key="allowance.id">
                            <td>{{ allowance.allowance_name }}</td>
                            <td>
                              {{ allowance.is_percentage === '1'
                                ? `${allowance.percentage}% (${formatCurrency(calculatePercentageAmount(allowance))})`
                                : formatCurrency(allowance.amount) }}
                            </td>
                            <td>
                                <span :class="allowance.is_taxable ? 'badge bg-success' : 'badge bg-warning'">
                                  {{ allowance.is_taxable ? 'Yes' : 'No' }}
                                </span>
                            </td>
                            <td v-if="isEditMode">
                              <button
                                  type="button"
                                  class="btn btn-sm btn-danger"
                                  @click="removeAllowance(allowance.id)"
                              >
                                <i class="mdi mdi-trash-can"></i>
                              </button>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="card">
                    <div class="card-header d-flex justify-content-between align-items-center">
                      <h5 class="card-title mb-0">Deductions</h5>
                      <button
                          v-if="isEditMode"
                          type="button"
                          class="btn btn-sm btn-primary"
                          @click="showAddDeductionModal"
                      >
                        <i class="mdi mdi-plus"></i>
                      </button>
                    </div>
                    <div class="card-body">
                      <div v-if="!allDeductions.length" class="text-center py-3">
                        <i class="mdi mdi-cash-minus text-muted font-size-24"></i>
                        <p class="mt-2 mb-0">No deductions configured</p>
                      </div>
                      <div v-else class="table-responsive">
                        <table class="table table-sm table-hover">
                          <thead class="table-light">
                          <tr>
                            <th>Type</th>
                            <th>Amount</th>
                            <th>Pre-tax</th>
                            <th>Status</th>
                            <th v-if="isEditMode">Actions</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="deduction in allDeductions" :key="deduction.id || deduction.deduction_type_id">
                            <td>
                              {{ deduction.deduction_name }}
                              <span v-if="isMandatoryDeduction(deduction)" class="badge bg-danger ms-1">Mandatory</span>
                            </td>
                            <td>
                              <span v-if="!deduction.configured" class="text-muted">
                                <em>{{ formatCurrency(getDeductionAmount(deduction)) }}</em>
                              </span>
                              <span v-else>
                                {{ formatCurrency(getDeductionAmount(deduction)) }}
                              </span>
                            </td>
                            <td>
                                <span :class="isPreTaxDeduction(deduction.deduction_name) ? 'badge bg-info' : 'badge bg-secondary'">
                                  {{ isPreTaxDeduction(deduction.deduction_name) ? 'Yes' : 'No' }}
                                </span>
                            </td>
                            <td>
                              <span v-if="!deduction.configured" class="badge bg-warning">Pending</span>
                              <span v-else class="badge bg-success">Active</span>
                            </td>
                            <td v-if="isEditMode">
                              <button
                                  v-if="!isMandatoryDeduction(deduction) && deduction.configured"
                                  type="button"
                                  class="btn btn-sm btn-danger"
                                  @click="removeDeduction(deduction.id)"
                              >
                                <i class="mdi mdi-trash-can"></i>
                              </button>
                              <span v-else-if="isMandatoryDeduction(deduction)" class="text-muted" title="Mandatory deductions cannot be removed">
                                <i class="mdi mdi-lock"></i>
                              </span>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Salary summary with detailed breakdown -->
              <div class="card mt-4">
                <div class="card-header">
                  <h5 class="card-title mb-0">Salary Summary (Detailed Breakdown)</h5>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6">
                      <div class="table-responsive">
                        <table class="table table-sm">
                          <tbody>
                          <tr>
                            <th style="width: 60%;">Basic Salary:</th>
                            <td class="text-end">{{ formatCurrency(salaryForm.basic_salary) }}</td>
                          </tr>
                          <tr>
                            <th>Total Allowances:</th>
                            <td class="text-end text-success">+ {{ formatCurrency(calculateTotalAllowances()) }}</td>
                          </tr>
                          <tr class="table-light">
                            <th>Gross Salary:</th>
                            <td class="text-end fw-bold">{{ formatCurrency(getGrossSalary()) }}</td>
                          </tr>
                          <tr>
                            <td colspan="2" class="pt-3"><strong>Pre-Tax Deductions:</strong></td>
                          </tr>
                          <tr>
                            <th class="ps-3">NSSF:</th>
                            <td class="text-end text-danger">- {{ formatCurrency(calculateNSSFAmount(salaryForm.basic_salary)) }}</td>
                          </tr>
                          <tr>
                            <th class="ps-3">SHIF (2.75%):</th>
                            <td class="text-end text-danger">- {{ formatCurrency(calculateSHIF()) }}</td>
                          </tr>
                          <tr>
                            <th class="ps-3">Housing Levy:</th>
                            <td class="text-end text-danger">- {{ formatCurrency(calculateHousingLevy()) }}</td>
                          </tr>
                          <tr class="table-light">
                            <th>Taxable Pay:</th>
                            <td class="text-end fw-bold">{{ formatCurrency(getTaxablePay()) }}</td>
                          </tr>
                          <tr>
                            <td colspan="2" class="pt-3"><strong>Tax Calculation:</strong></td>
                          </tr>
                          <tr>
                            <th class="ps-3">Income Tax (PAYE):</th>
                            <td class="text-end text-danger">- {{ formatCurrency(calculateIncomeTax()) }}</td>
                          </tr>
                          <tr>
                            <th class="ps-3">Personal Relief:</th>
                            <td class="text-end text-success">+ {{ formatCurrency(getPersonalRelief()) }}</td>
                          </tr>
                          <tr>
                            <th class="ps-3">Net PAYE:</th>
                            <td class="text-end text-danger">- {{ formatCurrency(getNetPAYE()) }}</td>
                          </tr>
                          <tr class="table-light">
                            <th>Pay After Tax:</th>
                            <td class="text-end fw-bold">{{ formatCurrency(getPayAfterTax()) }}</td>
                          </tr>
                          <tr v-if="calculateOtherDeductions() > 0">
                            <td colspan="2" class="pt-3"><strong>Other Deductions:</strong></td>
                          </tr>
                          <tr v-if="calculateOtherDeductions() > 0">
                            <th class="ps-3">Other Deductions:</th>
                            <td class="text-end text-danger">- {{ formatCurrency(calculateOtherDeductions()) }}</td>
                          </tr>
                          <tr class="table-active">
                            <th class="fs-5">Estimated Net Salary:</th>
                            <td class="text-end fw-bold fs-5 text-primary">{{ formatCurrency(calculateNetSalary()) }}</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="alert alert-info mb-3">
                        <h6><i class="mdi mdi-information-outline me-2"></i>Important Notes</h6>
                        <ul class="mb-0 ps-3">
                          <li>This is an estimated calculation matching KRA standards</li>
                          <li>NSSF calculated on Basic Salary only</li>
                          <li>SHIF calculated on Gross Salary (2.75%) - PRE-TAX</li>
                          <li>Housing Levy calculated on Gross Salary (1.5%) - PRE-TAX</li>
                          <li>Actual net salary finalized during payroll processing</li>
                        </ul>
                      </div>

                      <div class="alert alert-success mb-0">
                        <h6><i class="mdi mdi-calculator me-2"></i>Calculation Order</h6>
                        <ol class="mb-0 ps-3 small">
                          <li>Gross = Basic + Allowances</li>
                          <li>NSSF deducted from Basic Salary</li>
                          <li>SHIF deducted from Gross (PRE-TAX)</li>
                          <li>Housing Levy deducted from Gross (PRE-TAX)</li>
                          <li>Taxable Pay = Gross - NSSF - SHIF - Housing Levy</li>
                          <li>PAYE calculated on Taxable Pay</li>
                          <li>Personal Relief reduces PAYE</li>
                          <li>Other deductions applied (if any)</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Notes field -->
              <div class="card mt-4" v-if="isEditMode">
                <div class="card-header">
                  <h5 class="card-title mb-0">Notes</h5>
                </div>
                <div class="card-body">
                  <textarea
                      class="form-control"
                      v-model="salaryForm.notes"
                      rows="3"
                      placeholder="Add notes about this salary update (optional)"
                  ></textarea>
                </div>
              </div>

              <!-- Action buttons -->
              <div class="text-end mt-4" v-if="isEditMode">
                <button type="button" class="btn btn-secondary me-2" @click="toggleEditMode">
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" :disabled="submitLoading">
                  <span v-if="submitLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Save Salary Details
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Add Allowance Modal -->
  <div class="modal fade" id="addAllowanceModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Add Allowance</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="addAllowance">
            <div class="mb-3">
              <label class="form-label">Allowance Type <span class="text-danger">*</span></label>
              <select class="form-select" v-model="allowanceForm.allowance_type_id" required>
                <option value="">Select Allowance Type</option>
                <option v-for="type in allowanceTypes" :key="type.id" :value="type.id">
                  {{ type.allowance_name }}
                </option>
              </select>
            </div>

            <div class="mb-3">
              <label class="form-label">Amount Type <span class="text-danger">*</span></label>
              <div class="form-check">
                <input
                    class="form-check-input"
                    type="radio"
                    id="amount-fixed"
                    value="fixed"
                    v-model="allowanceForm.amount_type"
                >
                <label class="form-check-label" for="amount-fixed">Fixed Amount</label>
              </div>
              <div class="form-check">
                <input
                    class="form-check-input"
                    type="radio"
                    id="amount-percentage"
                    value="percentage"
                    v-model="allowanceForm.amount_type"
                >
                <label class="form-check-label" for="amount-percentage">Percentage of Basic Salary</label>
              </div>
            </div>

            <div class="mb-3" v-if="allowanceForm.amount_type === 'fixed'">
              <label class="form-label">Amount <span class="text-danger">*</span></label>
              <div class="input-group">
                <span class="input-group-text">{{ getCurrencySymbol() }}</span>
                <input type="number" class="form-control" v-model="allowanceForm.amount" required min="0" step="0.01">
              </div>
            </div>

            <div class="mb-3" v-if="allowanceForm.amount_type === 'percentage'">
              <label class="form-label">Percentage <span class="text-danger">*</span></label>
              <div class="input-group">
                <input type="number" class="form-control" v-model="allowanceForm.percentage" required min="0" max="100" step="0.01">
                <span class="input-group-text">%</span>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Effective Date <span class="text-danger">*</span></label>
              <input type="date" class="form-control" v-model="allowanceForm.effective_date" required>
            </div>

            <div class="mb-3">
              <label class="form-label">End Date</label>
              <input type="date" class="form-control" v-model="allowanceForm.end_date">
              <small class="text-muted">Leave blank if this is a permanent allowance</small>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button
              type="button"
              class="btn btn-primary"
              @click="addAllowance"
              :disabled="!isAllowanceFormValid || submitLoading"
          >
            <span v-if="submitLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Add Allowance
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Add Deduction Modal -->
  <div class="modal fade" id="addDeductionModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Add Deduction</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="addDeduction">
            <div class="mb-3">
              <label class="form-label">Deduction Type <span class="text-danger">*</span></label>
              <select class="form-select" v-model="deductionForm.deduction_type_id" required>
                <option value="">Select Deduction Type</option>
                <option v-for="type in deductionTypes" :key="type.id" :value="type.id">
                  {{ type.deduction_name }}
                </option>
              </select>
            </div>

            <div class="mb-3">
              <label class="form-label">Amount Type <span class="text-danger">*</span></label>
              <div class="form-check">
                <input
                    class="form-check-input"
                    type="radio"
                    id="deduction-fixed"
                    value="fixed"
                    v-model="deductionForm.amount_type"
                >
                <label class="form-check-label" for="deduction-fixed">Fixed Amount</label>
              </div>
              <div class="form-check">
                <input
                    class="form-check-input"
                    type="radio"
                    id="deduction-percentage"
                    value="percentage"
                    v-model="deductionForm.amount_type"
                >
                <label class="form-check-label" for="deduction-percentage">Percentage of Basic Salary</label>
              </div>
            </div>

            <div class="mb-3" v-if="deductionForm.amount_type === 'fixed'">
              <label class="form-label">Amount <span class="text-danger">*</span></label>
              <div class="input-group">
                <span class="input-group-text">{{ getCurrencySymbol() }}</span>
                <input type="number" class="form-control" v-model="deductionForm.amount" required min="0" step="0.01">
              </div>
            </div>

            <div class="mb-3" v-if="deductionForm.amount_type === 'percentage'">
              <label class="form-label">Percentage <span class="text-danger">*</span></label>
              <div class="input-group">
                <input type="number" class="form-control" v-model="deductionForm.percentage" required min="0" max="100" step="0.01">
                <span class="input-group-text">%</span>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Effective Date <span class="text-danger">*</span></label>
              <input type="date" class="form-control" v-model="deductionForm.effective_date" required>
            </div>

            <div class="mb-3">
              <label class="form-label">End Date</label>
              <input type="date" class="form-control" v-model="deductionForm.end_date">
              <small class="text-muted">Leave blank if this is a permanent deduction</small>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button
              type="button"
              class="btn btn-primary"
              @click="addDeduction"
              :disabled="!isDeductionFormValid || submitLoading"
          >
            <span v-if="submitLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Add Deduction
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";

export default {
  name: 'EmployeeSalary',
  props: {
    employeeId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      loading: false,
      isEditMode: false,
      submitLoading: false,
      hasExistingSalary: false,
      isManualPayGradeSelection: false,
      autoSelectedPayGrade: null,
      suggestedPayGrade: null,
      employeeData: {
        id: '',
        name: '',
        designation: '',
        department: '',
        hire_date: '',
        status: 'Active'
      },
      salaryForm: {
        basic_salary: 0,
        pay_grade_id: '',
        effective_date: '',
        payment_frequency: 'Monthly',
        payment_method: 'Bank Transfer',
        bank_name: '',
        bank_code: '',
        account_number: '',
        branch_code: '',
        mobile_money_number: '',
        currency: 'KES',
        is_active: true,
        notes: ''
      },
      salaryHistory: [],
      employeeAllowances: [],
      employeeDeductions: [],
      payGrades: [],
      allowanceTypes: [],
      deductionTypes: [],
      statutoryDeductions: [],
      deductionTypesMap: {},
      allowanceForm: {
        allowance_type_id: '',
        amount_type: 'fixed',
        amount: null,
        percentage: null,
        effective_date: new Date().toISOString().split('T')[0],
        end_date: ''
      },
      deductionForm: {
        deduction_type_id: '',
        amount_type: 'fixed',
        amount: null,
        percentage: null,
        effective_date: new Date().toISOString().split('T')[0],
        end_date: ''
      },
      modal: null,
      allowanceModal: null,
      deductionModal: null,
      mandatoryDeductions: [],
      needsToAddMandatoryDeductions: false
    };
  },
  computed: {
    isAllowanceFormValid() {
      if (!this.allowanceForm.allowance_type_id) return false;
      if (this.allowanceForm.amount_type === 'fixed' && !this.allowanceForm.amount) return false;
      if (this.allowanceForm.amount_type === 'percentage' && !this.allowanceForm.percentage) return false;
      if (!this.allowanceForm.effective_date) return false;
      return true;
    },
    isDeductionFormValid() {
      if (!this.deductionForm.deduction_type_id) return false;
      if (this.deductionForm.amount_type === 'fixed' && !this.deductionForm.amount) return false;
      if (this.deductionForm.amount_type === 'percentage' && !this.deductionForm.percentage) return false;
      if (!this.deductionForm.effective_date) return false;
      return true;
    },
    allDeductions() {
      const existingDeductionMap = {};
      this.employeeDeductions.forEach(deduction => {
        const typeId = deduction.deduction_type_id || deduction.id;
        existingDeductionMap[typeId] = { ...deduction, configured: true };
      });

      const allDeductionsList = [];
      this.employeeDeductions.forEach(deduction => {
        allDeductionsList.push({ ...deduction, configured: true });
      });

      this.mandatoryDeductions.forEach(mandatoryDeduction => {
        const typeId = mandatoryDeduction.id;
        if (!existingDeductionMap[typeId]) {
          const pendingDeduction = {
            id: mandatoryDeduction.id,
            deduction_type_id: mandatoryDeduction.id,
            deduction_name: mandatoryDeduction.deduction_name,
            is_percentage: mandatoryDeduction.is_percentage,
            percentage: mandatoryDeduction.default_percentage,
            amount: mandatoryDeduction.default_amount,
            is_pretax: mandatoryDeduction.is_pretax,
            is_mandatory: mandatoryDeduction.is_mandatory,
            description: mandatoryDeduction.description,
            configured: false
          };
          allDeductionsList.push(pendingDeduction);
        }
      });

      return allDeductionsList;
    },

    displayMandatoryDeductions() {
      const currentDeductionTypeIds = this.employeeDeductions.map(d => d.deduction_type_id || d.id);
      return this.statutoryDeductions.filter(deduction =>
          (deduction.is_mandatory === "1" || deduction.is_mandatory === 1) &&
          !currentDeductionTypeIds.includes(deduction.id)
      );
    },

    selectedPayGrade() {
      if (!this.salaryForm.pay_grade_id) return null;
      return this.payGrades.find(grade => grade.id === this.salaryForm.pay_grade_id);
    },

    isSalaryInPayGradeRange() {
      if (!this.selectedPayGrade || !this.salaryForm.basic_salary) return false;
      const salary = parseFloat(this.salaryForm.basic_salary);
      const minSalary = parseFloat(this.selectedPayGrade.min_salary);
      const maxSalary = parseFloat(this.selectedPayGrade.max_salary);

      if (minSalary === maxSalary) {
        const tolerance = minSalary * 0.05;
        return salary >= (minSalary - tolerance) && salary <= (minSalary + tolerance);
      }

      return salary >= minSalary && salary <= maxSalary;
    },

    regularPayGrades() {
      return this.payGrades.filter(grade => {
        const minSalary = parseFloat(grade.min_salary || 0);
        const maxSalary = parseFloat(grade.max_salary || 0);
        if (minSalary === 0 && maxSalary === 0) return false;
        const gradeType = (grade.grade_type || '').toLowerCase();
        return gradeType === 'salary' || gradeType === 'regular' || gradeType === '' || !grade.grade_type;
      });
    },

    dsaPayGrades() {
      return this.payGrades.filter(grade => {
        const minSalary = parseFloat(grade.min_salary || 0);
        const maxSalary = parseFloat(grade.max_salary || 0);
        if (minSalary === 0 && maxSalary === 0) return false;
        const gradeType = (grade.grade_type || '').toLowerCase();
        return gradeType === 'allowance' || gradeType === 'dsa' || gradeType === 'travel' || gradeType === 'other';
      });
    }
  },
  watch: {
    employeeId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchEmployeeSalary(newVal);
        }
      }
    }
  },
  mounted() {
    this.initModals();
    this.fetchPayGrades();
    this.fetchAllowanceTypes();
    this.fetchDeductionTypes();
    this.fetchStatutoryDeductions();
  },
  methods: {
    initModals() {
      this.$nextTick(() => {
        this.modal = new bootstrap.Modal(document.getElementById('employeeSalaryModal'));
        this.allowanceModal = new bootstrap.Modal(document.getElementById('addAllowanceModal'));
        this.deductionModal = new bootstrap.Modal(document.getElementById('addDeductionModal'));

        document.getElementById('employeeSalaryModal').addEventListener('hidden.bs.modal', this.resetEditMode);
        document.getElementById('addAllowanceModal').addEventListener('hidden.bs.modal', this.resetAllowanceForm);
        document.getElementById('addDeductionModal').addEventListener('hidden.bs.modal', this.resetDeductionForm);
      });
    },

    isPreTaxDeduction(deductionName) {
      const preTaxDeductions = ['NSSF', 'SHIF', 'Housing Levy', 'NHIF'];
      return preTaxDeductions.some(name => deductionName.includes(name));
    },

    getGrossSalary() {
      return parseFloat(this.salaryForm.basic_salary || 0) + this.calculateTotalAllowances();
    },

    getTaxablePay() {
      const gross = this.getGrossSalary();
      const nssf = this.calculateNSSFAmount(this.salaryForm.basic_salary);
      const shif = this.calculateSHIF();
      const housingLevy = this.calculateHousingLevy();
      return gross - nssf - shif - housingLevy;
    },

    calculateIncomeTax() {
      const taxablePay = this.getTaxablePay();
      const payeInfo = this.deductionTypesMap['PAYE'];
      if (!payeInfo || !payeInfo.tiers) return 0;

      let incomeTax = 0;
      for (const tier of payeInfo.tiers) {
        const minAmount = parseFloat(tier.min_amount);
        const maxAmount = tier.max_amount ? parseFloat(tier.max_amount) : Infinity;

        if (taxablePay > minAmount) {
          const taxableInBracket = Math.min(taxablePay, maxAmount) - minAmount;
          const fixedAmount = parseFloat(tier.fixed_amount || 0);
          const percentage = parseFloat(tier.percentage || 0) / 100;
          incomeTax = fixedAmount + (taxableInBracket * percentage);
        }

        if (taxablePay <= maxAmount) break;
      }
      return incomeTax;
    },

    getPersonalRelief() {
      const personalReliefInfo = this.deductionTypesMap['Personal Relief'];
      return personalReliefInfo ? Math.abs(parseFloat(personalReliefInfo.default_amount || 2400)) : 2400;
    },

    getNetPAYE() {
      return Math.max(0, this.calculateIncomeTax() - this.getPersonalRelief());
    },

    getPayAfterTax() {
      return this.getTaxablePay() - this.getNetPAYE();
    },

    calculateHousingLevy() {
      const gross = this.getGrossSalary();
      return gross * 0.015;
    },

    calculateSHIF() {
      const gross = this.getGrossSalary();
      return gross * 0.0275;
    },

    calculateOtherDeductions() {
      let total = 0;
      this.employeeDeductions.forEach(deduction => {
        const name = deduction.deduction_name;
        if (!name.includes('NSSF') && !name.includes('PAYE') &&
            !name.includes('SHIF') && !name.includes('Housing Levy') &&
            !name.includes('Personal Relief') && !name.includes('NHIF')) {
          total += this.getDeductionAmount(deduction);
        }
      });
      return total;
    },

    onBasicSalaryChange() {
      if (!this.isEditMode || this.isManualPayGradeSelection) return;
      const salary = parseFloat(this.salaryForm.basic_salary);
      if (!salary || salary <= 0) {
        this.suggestedPayGrade = null;
        this.autoSelectedPayGrade = null;
        this.salaryForm.pay_grade_id = '';
        return;
      }

      const matchingGrade = this.findPayGradeForSalary(salary);
      if (matchingGrade) {
        this.suggestedPayGrade = matchingGrade;
        this.autoSelectedPayGrade = matchingGrade;
        this.salaryForm.pay_grade_id = matchingGrade.id;
        this.isManualPayGradeSelection = false;
      } else {
        this.suggestedPayGrade = null;
        this.autoSelectedPayGrade = null;
        this.salaryForm.pay_grade_id = '';
      }
    },

    onPayGradeManualChange() {
      this.isManualPayGradeSelection = true;
      this.autoSelectedPayGrade = null;
      this.suggestedPayGrade = null;
    },

    findPayGradeForSalary(salary) {
      const regularGrades = this.regularPayGrades;
      const exactMatch = regularGrades.find(grade => {
        const minSalary = parseFloat(grade.min_salary);
        const maxSalary = parseFloat(grade.max_salary);

        if (minSalary === maxSalary) {
          const tolerance = minSalary * 0.05;
          return salary >= (minSalary - tolerance) && salary <= (minSalary + tolerance);
        }
        return salary >= minSalary && salary <= maxSalary;
      });

      if (exactMatch) return exactMatch;

      const closestMatch = regularGrades.find(grade => {
        const minSalary = parseFloat(grade.min_salary);
        const maxSalary = parseFloat(grade.max_salary);
        const tolerance = (maxSalary - minSalary) * 0.1;
        return salary >= (minSalary - tolerance) && salary <= (maxSalary + tolerance);
      });

      return closestMatch || null;
    },

    getPayGradeOptionClass(grade) {
      const salary = parseFloat(this.salaryForm.basic_salary);
      if (!salary || !this.isEditMode) return '';

      const minSalary = parseFloat(grade.min_salary);
      const maxSalary = parseFloat(grade.max_salary);

      if (minSalary === 0 && maxSalary === 0) return 'text-muted';

      const gradeType = (grade.grade_type || '').toLowerCase();
      const isNonSalary = gradeType === 'allowance' || gradeType === 'dsa' || gradeType === 'travel' || gradeType === 'other';

      if (isNonSalary) return 'text-muted font-italic';

      if (minSalary === maxSalary) {
        const tolerance = minSalary * 0.05;
        if (salary >= (minSalary - tolerance) && salary <= (minSalary + tolerance)) {
          return 'bg-light-success text-success';
        }
      } else if (salary >= minSalary && salary <= maxSalary) {
        return 'bg-light-success text-success';
      }

      return '';
    },

    getSalaryPositionClass() {
      if (!this.selectedPayGrade || !this.isSalaryInPayGradeRange) return 'bg-secondary';
      const percentage = this.getSalaryPositionPercentage();
      if (percentage <= 33) return 'bg-info';
      if (percentage <= 66) return 'bg-warning';
      return 'bg-success';
    },

    getSalaryPositionPercentage() {
      if (!this.selectedPayGrade || !this.salaryForm.basic_salary) return 0;

      const salary = parseFloat(this.salaryForm.basic_salary);
      const minSalary = parseFloat(this.selectedPayGrade.min_salary);
      const maxSalary = parseFloat(this.selectedPayGrade.max_salary);

      if (maxSalary === minSalary) return salary === minSalary ? 100 : 0;

      const percentage = ((salary - minSalary) / (maxSalary - minSalary)) * 100;
      return Math.min(100, Math.max(0, Math.round(percentage)));
    },

    showModal() {
      if (this.modal) {
        this.modal.show();
      } else {
        this.initModals();
        setTimeout(() => {
          if (this.modal) this.modal.show();
        }, 100);
      }
    },
    hideModal() {
      if (this.modal) this.modal.hide();
    },
    showAddAllowanceModal() {
      if (this.allowanceModal) this.allowanceModal.show();
    },
    hideAddAllowanceModal() {
      if (this.allowanceModal) this.allowanceModal.hide();
    },
    showAddDeductionModal() {
      if (this.deductionModal) this.deductionModal.show();
    },
    hideAddDeductionModal() {
      if (this.deductionModal) this.deductionModal.hide();
    },
    resetEditMode() {
      this.isEditMode = false;
      this.needsToAddMandatoryDeductions = false;
      this.isManualPayGradeSelection = false;
      this.autoSelectedPayGrade = null;
      this.suggestedPayGrade = null;
    },
    resetAllowanceForm() {
      this.allowanceForm = {
        allowance_type_id: '',
        amount_type: 'fixed',
        amount: null,
        percentage: null,
        effective_date: new Date().toISOString().split('T')[0],
        end_date: ''
      };
    },
    resetDeductionForm() {
      this.deductionForm = {
        deduction_type_id: '',
        amount_type: 'fixed',
        amount: null,
        percentage: null,
        effective_date: new Date().toISOString().split('T')[0],
        end_date: ''
      };
    },

    fetchStatutoryDeductions() {
      const data = new FormData();
      data.append("function", "getStatutoryDeductions");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.statutoryDeductions = res.data.data.statutoryDeductions;
              this.deductionTypesMap = {};
              this.statutoryDeductions.forEach(deduction => {
                this.deductionTypesMap[deduction.deduction_name] = deduction;
              });
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
          });
    },

    fetchEmployeeSalary(employeeId) {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getEmployeeSalary");
      data.append("employeeId", employeeId);

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              const responseData = res.data.data;
              this.employeeData = responseData.employee || this.employeeData;
              this.hasExistingSalary = !!(responseData.currentSalary && responseData.currentSalary.id);

              if (responseData.currentSalary) {
                this.salaryForm = {
                  basic_salary: responseData.currentSalary.basic_salary || 0,
                  pay_grade_id: responseData.currentSalary.pay_grade_id || '',
                  effective_date: responseData.currentSalary.effective_date || new Date().toISOString().split('T')[0],
                  payment_frequency: responseData.currentSalary.payment_frequency || 'Monthly',
                  payment_method: responseData.currentSalary.payment_method || 'Bank Transfer',
                  bank_name: responseData.currentSalary.bank_name || '',
                  bank_code: responseData.currentSalary.bank_code || '',
                  account_number: responseData.currentSalary.account_number || '',
                  branch_code: responseData.currentSalary.branch_code || '',
                  mobile_money_number: responseData.currentSalary.mobile_money_number || '',
                  currency: responseData.currentSalary.currency || 'KES',
                  is_active: responseData.currentSalary.is_active === '1' || responseData.currentSalary.is_active === 1 || responseData.currentSalary.is_active === true,
                  notes: responseData.currentSalary.notes || ''
                };
              }

              this.salaryHistory = responseData.salaryHistory || [];
              this.employeeAllowances = responseData.allowances || [];
              this.employeeDeductions = responseData.deductions || [];

              this.checkMandatoryDeductions();
              this.showModal();
            } else {
              alert(res.data.message || "Failed to load employee salary information");
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API Error:", error);
            alert("An error occurred while loading employee salary information");
          });
    },

    fetchPayGrades() {
      const data = new FormData();
      data.append("function", "getPayGrades");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.payGrades = res.data.data.payGrades || [];
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
          });
    },

    fetchAllowanceTypes() {
      const data = new FormData();
      data.append("function", "getAllowanceTypes");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.allowanceTypes = res.data.data.allowanceTypes || [];
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
          });
    },

    fetchDeductionTypes() {
      const data = new FormData();
      data.append("function", "getDeductionTypes");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.deductionTypes = res.data.data.deductionTypes || [];
              this.mandatoryDeductions = this.deductionTypes.filter(deduction =>
                  deduction.is_mandatory === "1" || deduction.is_mandatory === 1
              );
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
          });
    },

    toggleEditMode() {
      this.isEditMode = !this.isEditMode;
      if (this.isEditMode) {
        this.isManualPayGradeSelection = false;
        this.autoSelectedPayGrade = null;
        this.suggestedPayGrade = null;
        this.checkMandatoryDeductions();

        if (this.salaryForm.basic_salary) {
          this.$nextTick(() => {
            this.onBasicSalaryChange();
          });
        }
      }
    },

    checkMandatoryDeductions() {
      const currentDeductionTypeIds = this.employeeDeductions.map(d => d.deduction_type_id || d.id);
      const missingMandatory = this.mandatoryDeductions.filter(deduction =>
          !currentDeductionTypeIds.includes(deduction.id)
      );
      this.needsToAddMandatoryDeductions = missingMandatory.length > 0;
    },

    isMandatoryDeduction(deduction) {
      const deductionTypeId = deduction.deduction_type_id || deduction.id;
      return this.mandatoryDeductions.some(mandatory => mandatory.id === String(deductionTypeId));
    },

    getCurrencySymbol() {
      return this.salaryForm.currency || 'KES';
    },

    formatCurrency(value) {
      const currency = this.salaryForm.currency || 'KES';
      const numValue = parseFloat(value) || 0;

      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
      }).format(numValue);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    },

    calculateServiceYears(hireDate) {
      if (!hireDate) return 0;

      const hire = new Date(hireDate);
      const today = new Date();
      const diffTime = today - hire;
      const diffYears = diffTime / (1000 * 60 * 60 * 24 * 365.25);

      return Math.floor(diffYears * 10) / 10;
    },

    getSalaryChangeClass(history, index) {
      if (index === this.salaryHistory.length - 1) return '';

      const nextIndex = index - 1;
      if (nextIndex < 0 || nextIndex >= this.salaryHistory.length) return '';

      const currentSalary = parseFloat(history.basic_salary);
      const nextSalary = parseFloat(this.salaryHistory[nextIndex].basic_salary);

      if (currentSalary < nextSalary) {
        return 'text-danger';
      } else if (currentSalary > nextSalary) {
        return 'text-success';
      }

      return '';
    },

    formatSalaryChange(history, index) {
      if (index === this.salaryHistory.length - 1) return '';

      const nextIndex = index - 1;
      if (nextIndex < 0 || nextIndex >= this.salaryHistory.length) return '';

      const currentSalary = parseFloat(history.basic_salary);
      const nextSalary = parseFloat(this.salaryHistory[nextIndex].basic_salary);
      const diff = nextSalary - currentSalary;

      if (diff === 0) return 'No change';

      const percentChange = (diff / currentSalary) * 100;
      const sign = diff > 0 ? '+' : '';

      return `${sign}${diff.toFixed(2)} (${sign}${percentChange.toFixed(2)}%)`;
    },

    calculatePercentageAmount(item) {
      if (!item.is_percentage || !item.percentage) return 0;
      return (parseFloat(this.salaryForm.basic_salary) * parseFloat(item.percentage)) / 100;
    },

    calculateTotalAllowances() {
      let total = 0;
      this.employeeAllowances.forEach(allowance => {
        if (allowance.is_percentage === '1') {
          total += this.calculatePercentageAmount(allowance);
        } else {
          total += parseFloat(allowance.amount || 0);
        }
      });
      return total;
    },

    getDeductionAmount(deduction) {
      const deductionName = deduction.deduction_name;
      const basicSalary = parseFloat(this.salaryForm.basic_salary || 0);
      const grossSalary = basicSalary + this.calculateTotalAllowances();

      if (deductionName.includes('NSSF') && !deductionName.includes('Employer')) {
        return this.calculateNSSFAmount(basicSalary);
      }

      if (deductionName === 'SHIF' || deductionName.includes('SHIF')) {
        return this.calculateSHIF();
      }

      if (deductionName.includes('Housing Levy') && !deductionName.includes('Employer')) {
        return this.calculateHousingLevy();
      }

      if (deductionName === 'NHIF') {
        return this.calculateTieredDeduction('NHIF', grossSalary);
      }

      if (deductionName === 'PAYE') {
        return this.getNetPAYE();
      }

      if (deductionName === 'Personal Relief') {
        return 0;
      }

      if (deduction.is_percentage === '1') {
        let amount = this.calculatePercentageAmount(deduction);
        const statutoryInfo = this.deductionTypesMap[deductionName];
        if (statutoryInfo && statutoryInfo.max_contribution) {
          amount = Math.min(amount, parseFloat(statutoryInfo.max_contribution));
        }
        return amount;
      }

      return parseFloat(deduction.amount || 0);
    },

    calculateTotalDeductions() {
      const nssf = this.calculateNSSFAmount(this.salaryForm.basic_salary);
      const shif = this.calculateSHIF();
      const housingLevy = this.calculateHousingLevy();
      const paye = this.getNetPAYE();
      const others = this.calculateOtherDeductions();

      return nssf + shif + housingLevy + paye + others;
    },

    calculateNSSFAmount(basicSalary) {
      const salary = parseFloat(basicSalary);

      const nssfDeductions = Object.values(this.deductionTypesMap).filter(d =>
          d.deduction_name.includes('NSSF') && d.deduction_name.includes('Tier') && !d.deduction_name.includes('Employer')
      );

      if (nssfDeductions.length === 0) {
        console.warn('No NSSF tier deductions found');
        return 0;
      }

      nssfDeductions.sort((a, b) => {
        const aTier = a.deduction_name.includes('Tier I') || a.deduction_name.includes('Tier 1') ? 1 : 2;
        const bTier = b.deduction_name.includes('Tier I') || b.deduction_name.includes('Tier 1') ? 1 : 2;
        return aTier - bTier;
      });

      let totalNSSF = 0;

      nssfDeductions.forEach(tierDeduction => {
        const minLimit = parseFloat(tierDeduction.min_income_limit || 0);
        const maxLimit = parseFloat(tierDeduction.max_income_limit || Infinity);
        const rate = parseFloat(tierDeduction.default_percentage || 0) / 100;
        const maxContribution = parseFloat(tierDeduction.max_contribution || Infinity);

        if (salary > minLimit) {
          const applicableSalary = Math.min(salary, maxLimit) - minLimit;
          const contribution = Math.min(applicableSalary * rate, maxContribution);
          totalNSSF += contribution;
        }
      });

      return totalNSSF;
    },

    calculateTieredDeduction(deductionName, grossSalary) {
      const deductionInfo = this.deductionTypesMap[deductionName];

      if (!deductionInfo || !deductionInfo.tiers) return 0;

      for (const tier of deductionInfo.tiers) {
        const minAmount = parseFloat(tier.min_amount);
        const maxAmount = tier.max_amount ? parseFloat(tier.max_amount) : Infinity;

        if (grossSalary >= minAmount && grossSalary <= maxAmount) {
          if (tier.fixed_amount) {
            return parseFloat(tier.fixed_amount);
          } else if (tier.percentage) {
            const baseAmount = parseFloat(tier.fixed_amount || 0);
            const percentageAmount = (grossSalary - minAmount) * (parseFloat(tier.percentage) / 100);
            return baseAmount + percentageAmount;
          }
        }
      }

      return 0;
    },

    calculateNetSalary() {
      const basicSalary = parseFloat(this.salaryForm.basic_salary || 0);
      const totalAllowances = this.calculateTotalAllowances();
      const totalDeductions = this.calculateTotalDeductions();

      return basicSalary + totalAllowances - totalDeductions;
    },

    async addMandatoryDeductions() {
      const effectiveDate = this.salaryForm.effective_date;
      const currentDeductionTypeIds = this.employeeDeductions.map(d => d.deduction_type_id || d.id);

      const deductionsToAdd = this.mandatoryDeductions.filter(deduction =>
          !currentDeductionTypeIds.includes(deduction.id)
      );

      for (const deduction of deductionsToAdd) {
        const calculationMethod = this.deductionTypesMap[deduction.deduction_name]?.calculation_method || deduction.calculation_method;

        const deductionData = {
          deduction_type_id: deduction.id,
          is_percentage: calculationMethod === 'Percentage',
          percentage: deduction.default_percentage,
          amount: deduction.default_amount,
          effective_date: effectiveDate
        };

        if (calculationMethod === 'Tiered' || calculationMethod === 'Custom') {
          deductionData.percentage = null;
          deductionData.amount = null;
          deductionData.is_percentage = false;
        } else if (calculationMethod === 'Fixed') {
          deductionData.is_percentage = false;
          deductionData.percentage = null;
        }

        await this.addMandatoryDeduction(deductionData);
      }
    },

    async addMandatoryDeduction(deductionData) {
      const data = new FormData();
      data.append("function", "addEmployeeDeduction");
      data.append("employee_id", this.employeeData.id);
      data.append("deduction_type_id", deductionData.deduction_type_id);
      data.append("effective_date", deductionData.effective_date);
      data.append("is_mandatory", "1");

      if (deductionData.is_percentage) {
        data.append("is_percentage", "1");
        if (deductionData.percentage !== null) {
          data.append("percentage", deductionData.percentage);
        }
      } else {
        data.append("is_percentage", "0");
        if (deductionData.amount !== null) {
          data.append("amount", deductionData.amount);
        }
      }

      try {
        const res = await execute(data, authUrl);
        if (!res.data.success) {
          console.error(`Failed to add mandatory deduction ${deductionData.deduction_type_id}:`, res.data.message);
        }
      } catch (error) {
        console.error(`Error adding mandatory deduction ${deductionData.deduction_type_id}:`, error);
      }
    },

    async saveSalary() {
      if (!this.employeeData.id) return;

      this.submitLoading = true;

      if (!this.hasExistingSalary || this.needsToAddMandatoryDeductions) {
        await this.addMandatoryDeductions();
      }

      const data = new FormData();
      data.append("function", "updateEmployeeSalary");
      data.append("employeeId", this.employeeData.id);
      data.append("basic_salary", this.salaryForm.basic_salary);
      data.append("effective_date", this.salaryForm.effective_date);

      if (this.salaryForm.pay_grade_id) {
        data.append("pay_grade_id", this.salaryForm.pay_grade_id);
      }

      data.append("payment_frequency", this.salaryForm.payment_frequency);
      data.append("currency", this.salaryForm.currency);
      data.append("payment_method", this.salaryForm.payment_method);
      data.append("is_active", this.salaryForm.is_active ? 1 : 0);

      if (this.salaryForm.bank_name) data.append("bank_name", this.salaryForm.bank_name);
      if (this.salaryForm.bank_code) data.append("bank_code", this.salaryForm.bank_code);
      if (this.salaryForm.account_number) data.append("account_number", this.salaryForm.account_number);
      if (this.salaryForm.branch_code) data.append("branch_code", this.salaryForm.branch_code);
      if (this.salaryForm.mobile_money_number) data.append("mobile_money_number", this.salaryForm.mobile_money_number);
      if (this.salaryForm.notes) data.append("notes", this.salaryForm.notes);

      execute(data, authUrl)
          .then((res) => {
            this.submitLoading = false;

            if (res.data.success) {
              alert(res.data.message || "Salary updated successfully");
              this.fetchEmployeeSalary(this.employeeData.id);
              this.isEditMode = false;
              this.$emit('salary-updated');
            } else {
              alert(res.data.message || "Failed to update salary");
            }
          })
          .catch((error) => {
            this.submitLoading = false;
            console.error("API Error:", error);
            alert("An error occurred while updating salary");
          });
    },

    addAllowance() {
      if (!this.isAllowanceFormValid || !this.employeeData.id) return;

      this.submitLoading = true;

      const data = new FormData();
      data.append("function", "addEmployeeAllowance");
      data.append("employee_id", this.employeeData.id);
      data.append("allowance_type_id", this.allowanceForm.allowance_type_id);
      data.append("effective_date", this.allowanceForm.effective_date);

      if (this.allowanceForm.amount_type === 'fixed') {
        data.append("amount", this.allowanceForm.amount);
        data.append("is_percentage", 0);
      } else {
        data.append("percentage", this.allowanceForm.percentage);
        data.append("is_percentage", 1);
      }

      if (this.allowanceForm.end_date) {
        data.append("end_date", this.allowanceForm.end_date);
      }

      execute(data, authUrl)
          .then((res) => {
            this.submitLoading = false;

            if (res.data.success) {
              alert(res.data.message || "Allowance added successfully");
              this.fetchEmployeeSalary(this.employeeData.id);
              this.hideAddAllowanceModal();
              this.resetAllowanceForm();
            } else {
              alert(res.data.message || "Failed to add allowance");
            }
          })
          .catch((error) => {
            this.submitLoading = false;
            console.error("API Error:", error);
            alert("An error occurred while adding allowance");
          });
    },

    addDeduction() {
      if (!this.isDeductionFormValid || !this.employeeData.id) return;

      this.submitLoading = true;

      const data = new FormData();
      data.append("function", "addEmployeeDeduction");
      data.append("employee_id", this.employeeData.id);
      data.append("deduction_type_id", this.deductionForm.deduction_type_id);
      data.append("effective_date", this.deductionForm.effective_date);

      if (this.deductionForm.amount_type === 'fixed') {
        data.append("amount", this.deductionForm.amount);
        data.append("is_percentage", 0);
      } else {
        data.append("percentage", this.deductionForm.percentage);
        data.append("is_percentage", 1);
      }

      if (this.deductionForm.end_date) {
        data.append("end_date", this.deductionForm.end_date);
      }

      execute(data, authUrl)
          .then((res) => {
            this.submitLoading = false;

            if (res.data.success) {
              alert(res.data.message || "Deduction added successfully");
              this.fetchEmployeeSalary(this.employeeData.id);
              this.hideAddDeductionModal();
              this.resetDeductionForm();
            } else {
              alert(res.data.message || "Failed to add deduction");
            }
          })
          .catch((error) => {
            this.submitLoading = false;
            console.error("API Error:", error);
            alert("An error occurred while adding deduction");
          });
    },

    removeAllowance(allowanceId) {
      if (!confirm("Are you sure you want to remove this allowance?")) return;

      const data = new FormData();
      data.append("function", "removeEmployeeAllowance");
      data.append("employee_id", this.employeeData.id);
      data.append("allowance_id", allowanceId);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              alert(res.data.message || "Allowance removed successfully");
              this.fetchEmployeeSalary(this.employeeData.id);
            } else {
              alert(res.data.message || "Failed to remove allowance");
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
            alert("An error occurred while removing allowance");
          });
    },

    removeDeduction(deductionId) {
      if (!confirm("Are you sure you want to remove this deduction?")) return;

      const data = new FormData();
      data.append("function", "removeEmployeeDeduction");
      data.append("employee_id", this.employeeData.id);
      data.append("deduction_id", deductionId);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              alert(res.data.message || "Deduction removed successfully");
              this.fetchEmployeeSalary(this.employeeData.id);
            } else {
              alert(res.data.message || "Failed to remove deduction");
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
            alert("An error occurred while removing deduction");
          });
    }
  }
};
</script>

<style scoped>
.form-check {
  margin-bottom: 0.5rem;
}

.table th {
  font-weight: 600;
}

.avatar-md {
  height: 4.5rem;
  width: 4.5rem;
}

.avatar-title {
  align-items: center;
  display: flex;
  font-weight: 500;
  height: 100%;
  justify-content: center;
  width: 100%;
}

.badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.bg-light-success {
  background-color: #d4edda !important;
}

.progress {
  border-radius: 10px;
}

.progress-bar {
  border-radius: 10px;
  transition: width 0.3s ease;
}

.text-success small {
  font-weight: 500;
}

.text-warning small {
  font-weight: 500;
}

.text-danger small {
  font-weight: 500;
}

.alert-success {
  border-left: 4px solid #28a745;
}

.input-group input:focus {
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

select option.bg-light-success {
  background-color: #d4edda;
  color: #155724;
  font-weight: 500;
}

select option.text-muted {
  color: #6c757d;
  font-style: italic;
}

select option.font-italic {
  font-style: italic;
}

select optgroup {
  font-weight: bold;
  color: #495057;
}

select optgroup option {
  font-weight: normal;
  padding-left: 1rem;
}
</style>